"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTranslation } from '@/app/i18n/client';
import { onConsentChange } from '@/lib/attribution';

const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  // Mounts hidden and appears on the client only for un-consented visitors.
  // The banner is position:fixed, so appearing after the layout has settled
  // adds ZERO layout shift (a bottom-pinned fixed element that materialises
  // mid-load otherwise registers as CLS). It is deliberately NOT server-
  // rendered: the hero <h1> is the LCP element, so the banner never needs to
  // be in the first paint, and keeping it out avoids the CLS entirely.
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const acceptAll = () => {
    // Store consent in localStorage
    localStorage.setItem('cookie-consent', 'full');
    // Captures attribution and loads the Google tag (components/TrackingScripts.tsx).
    onConsentChange('full');
    setIsVisible(false);
  };

  const acceptEssential = () => {
    // Store essential-only consent in localStorage
    localStorage.setItem('cookie-consent', 'essential');
    onConsentChange('essential');
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg z-50 border-t border-gray-200 px-4 py-3 md:py-4">
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 md:gap-4">
          <div className="flex-1 pr-8 md:pr-6">
            <h3 className="text-base md:text-lg font-semibold mb-0.5 md:mb-1" suppressHydrationWarning>{t('components.cookieConsent.title')}</h3>
            <p className="text-gray-600 text-xs md:text-sm leading-snug" suppressHydrationWarning>
              {t('components.cookieConsent.body')}{' '}
              <Link href="/cookies" className="text-primary-500 hover:text-primary-600 underline">
                {t('components.cookieConsent.policyLink')}
              </Link>{' '}
              {t('components.cookieConsent.policySuffix')}
            </p>
          </div>
          <div className="flex flex-row gap-3 self-end mt-1 md:mt-0">
            {/* Deny and Accept All share one variant on purpose: equal prominence. */}
            <Button
              variant="default"
              size="sm"
              onClick={acceptEssential}
              suppressHydrationWarning
            >
              {t('components.cookieConsent.deny')}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={acceptAll}
              suppressHydrationWarning
            >
              {t('components.cookieConsent.acceptAll')}
            </Button>
            <button
              className="text-gray-400 hover:text-gray-500 p-1 rounded-full focus:outline-none absolute top-2 right-2 md:hidden"
              onClick={() => setIsVisible(false)}
              aria-label={t('components.cookieConsent.close')}
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { CookieConsent };